import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { ChangeHighlighter } from 'src/app/graph/changeLogPage/changeHighlighter/ChangeHighlighter';
import { DetailedChangeIcon } from 'src/app/graph/changeLogPage/changeIcon/ChangeIcon';
import { ChangesCounters } from 'src/app/graph/changeLogPage/changesCounters/ChangesCounters';
import { getTypeOfChangeByChangeCodeAndTypeOfThing } from 'src/app/graph/changeLogPage/getTypeOfChangeByChangeCodeAndTypeOfThing';
import { getSubjectSlugByGraphQLType } from 'src/app/graph/documentationPage/schemaPage/getSubjectSlugByGraphQLType';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import {
  changelogSingleChangeRouteConfig,
  variantRouteConfig,
} from 'src/app/graph/routes';
import { splitPrintedTypeString } from 'src/components/common/formattedTypeString/FormattedTypeString';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

/* Humaira - This component could potentially be turned into a generic changelog entry component
          if we end up using the changelog entry info in other places in the future?
          Right now there's the original ChangeLogEntry component, and then this one
          which is based on that with a few minor changes. Make a generic component that can take
          as props basic width/height info, along with a boolean for showing more detailed field info?
           */

export const ChangeLogSectionEntry = ({
  historyItem,
  graphqlSchema,
  limitLines,
}: {
  graphqlSchema: GraphQLSchema;
  historyItem: GraphQLTypes.SchemaTagDiffFragment;
  limitLines: number;
}) => {
  const typeMap = graphqlSchema.getTypeMap();
  const dateString = new Date(historyItem.publishedAt).toLocaleDateString(
    undefined,
    {
      month: 'long',
      day: 'numeric',
    },
  );
  const location = useLocation();
  const { locationToReference } = useInternalGraphLinking();

  const data = useMemo(() => {
    let count = 0;
    return Object.entries(
      _.groupBy(
        historyItem.diffToPrevious?.changes,
        (change) => change.parentNode?.name,
      ),
    )
      .map(([typeName, changesOnType]) => {
        const changesRelevantToTypeAndNotFields = changesOnType.filter(
          (change) =>
            getTypeOfChangeByChangeCodeAndTypeOfThing(change.code, 'type') !==
            'SOMETHING_INSIDE_THIS_CHANGED',
        );
        const changesInFields = _.difference(
          changesOnType,
          changesRelevantToTypeAndNotFields,
        );
        return [
          typeName,
          Object.entries(
            _.groupBy(changesInFields, (change) => change.childNode?.name),
          ),
          changesRelevantToTypeAndNotFields,
        ] as const;
      })
      .filter(([, changes]) => {
        const shouldShow = count <= limitLines;
        count += 1 + changes.length;
        return shouldShow;
      });
  }, [historyItem.diffToPrevious?.changes, limitLines]);
  return (
    <div className="mt-1.5 flex h-20 w-full flex-col">
      <div className="shrink-0 text-base lg:block" style={{ fontWeight: 400 }}>
        <div className="mb-1.5 flex items-baseline text-secondary">
          <Tooltip
            label="Go to full Changelog entry for this date"
            placement="top"
          >
            <NavLink
              to={changelogSingleChangeRouteConfig.locationFrom({
                location,
                fromRouteConfig: variantRouteConfig,
                patch: { version: historyItem.id },
              })}
              activeStyle={{
                textDecoration: 'none',
                color: '#333333',
              }}
              className="cursor-pointer hover:text-link"
              data-analytics-label="Sidebar: Changelog: Go to full Changelog entry for this date"
              data-analytics-category="Variant Home"
            >
              {dateString},
            </NavLink>
          </Tooltip>
          <LocalTimestamp
            className="ml-1 lowercase hover:text-link"
            value={historyItem.publishedAt}
            format="time-only"
          />
          <div className="ml-auto flex flex-wrap">
            {historyItem.diffToPrevious?.changes && (
              <ChangesCounters changes={historyItem.diffToPrevious?.changes} />
            )}
          </div>
        </div>
      </div>
      <div className="grow">
        <ul
          data-changelog-type-changes-in-history-item
          className="flex flex-col leading-loose"
        >
          {data.map(
            ([typeName, changes, changesRelevantToTypeAndNotFields]) => {
              const typeOfChangeForType = changesRelevantToTypeAndNotFields[0]
                ? getTypeOfChangeByChangeCodeAndTypeOfThing(
                    changesRelevantToTypeAndNotFields[0].code,
                    'type',
                  )
                : null;

              const type: (typeof typeMap)[string] | undefined | null =
                typeMap[typeName];

              return (
                <li key={typeName} className="my-1.5">
                  <h5 className="flex items-center">
                    <DetailedChangeIcon
                      changeType={typeOfChangeForType}
                      className="mr-2"
                    />
                    <ChangeHighlighter
                      typeOfChange={typeOfChangeForType}
                      size="small"
                      className="truncate"
                    >
                      {type ? (
                        <Tooltip
                          label="Open in Schema Reference"
                          placement="top"
                        >
                          <NavLink
                            className="hover:underline"
                            to={locationToReference({
                              category: getSubjectSlugByGraphQLType(type),
                              graphqlTypeName: typeName,
                            })}
                            data-analytics-label="Sidebar: Changelog: Open in Schema Reference"
                            data-analytics-category="Variant Home"
                          >
                            {typeName}
                          </NavLink>
                        </Tooltip>
                      ) : (
                        typeName
                      )}
                    </ChangeHighlighter>
                  </h5>
                  <ul data-changelog-changes-in-type className="ml-6">
                    {changes.map(([fieldName, changesOnField]) => {
                      const changesInArguments = changesOnField.filter(
                        (change) =>
                          getTypeOfChangeByChangeCodeAndTypeOfThing(
                            change.code,
                            'field',
                          ) === 'SOMETHING_INSIDE_THIS_CHANGED',
                      );
                      const changesRelevantToFieldAndNotArguments =
                        _.difference(changesOnField, changesInArguments);

                      const fieldChangeType =
                        changesRelevantToFieldAndNotArguments[0]
                          ? getTypeOfChangeByChangeCodeAndTypeOfThing(
                              changesRelevantToFieldAndNotArguments[0].code,
                              'field',
                            )
                          : null;

                      const field =
                        type &&
                        'getFields' in type &&
                        type.getFields()[fieldName];

                      return (
                        <li key={fieldName} className="mt-2.5">
                          <h5 className="flex items-center">
                            <DetailedChangeIcon
                              changeType={fieldChangeType}
                              className="mr-2"
                            />
                            <ChangeHighlighter
                              size="small"
                              className="mr-1 truncate"
                              typeOfChange={
                                changesRelevantToFieldAndNotArguments[0]
                                  ?.code === 'FIELD_CHANGED_TYPE' ||
                                changesRelevantToFieldAndNotArguments[0]
                                  ?.code ===
                                  'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE'
                                  ? null
                                  : fieldChangeType
                              }
                            >
                              {type && field ? (
                                <Tooltip
                                  label="Open in Schema Reference"
                                  placement="top"
                                >
                                  <NavLink
                                    className="hover:underline"
                                    to={locationToReference({
                                      category:
                                        getSubjectSlugByGraphQLType(type),
                                      graphqlTypeName: typeName,
                                      linkedGraphqlEntity: fieldName,
                                    })}
                                    data-analytics-label="Sidebar: Changelog: Open in Schema Reference"
                                    data-analytics-category="Variant Home"
                                  >
                                    {fieldName}
                                  </NavLink>
                                </Tooltip>
                              ) : (
                                fieldName
                              )}
                              {(changesRelevantToFieldAndNotArguments[0]
                                ?.code === 'FIELD_CHANGED_TYPE' ||
                                changesRelevantToFieldAndNotArguments[0]
                                  ?.code ===
                                  'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE') &&
                                ':'}
                            </ChangeHighlighter>
                          </h5>
                          {changesRelevantToFieldAndNotArguments.length ? (
                            changesRelevantToFieldAndNotArguments.map(
                              (change) => {
                                return (
                                  <React.Fragment key={change.code}>
                                    {(change.code === 'FIELD_CHANGED_TYPE' ||
                                      change.code ===
                                        'FIELD_ON_INPUT_OBJECT_CHANGED_TYPE') && (
                                      <ChangeHighlighter
                                        size="small"
                                        className="mr-1"
                                        typeOfChange="THIS_CHANGED"
                                      >
                                        {change.childNode?.printedType &&
                                          splitPrintedTypeString(
                                            change.childNode?.printedType,
                                          ).map((segment, i) => {
                                            if (i !== 1) {
                                              // this is wrapper stuff like ([!]!)
                                              return segment;
                                            }
                                            const typeData = typeMap[segment];
                                            if (!typeData) {
                                              // can't find the type in the current schema
                                              return segment;
                                            }

                                            return (
                                              <Tooltip
                                                label="Open in Schema Reference"
                                                placement="top"
                                                key={segment}
                                              >
                                                <NavLink
                                                  key={segment}
                                                  to={locationToReference({
                                                    category:
                                                      getSubjectSlugByGraphQLType(
                                                        typeData,
                                                      ),
                                                    graphqlTypeName: segment,
                                                  })}
                                                  className="hover:underline"
                                                  data-analytics-label="Sidebar: Changelog: Open in Schema Reference"
                                                  data-analytics-category="Variant Home"
                                                >
                                                  {segment}
                                                </NavLink>
                                              </Tooltip>
                                            );
                                          })}
                                      </ChangeHighlighter>
                                    )}
                                    {/* Potentially add this back if we want the mini log to be more descriptive
                            <StyledChangeDescription className="mx-1">
                              {change.code.replace(/_/g, ' ')} 
                            </StyledChangeDescription> */}
                                  </React.Fragment>
                                );
                              },
                            )
                          ) : (
                            <StyledChangeDescription>
                              field modified
                            </StyledChangeDescription>
                          )}
                          <ul
                            data-changelog-field-argument-changes
                            className="ml-6"
                          >
                            {changesInArguments.map((change) => {
                              const argChangeType =
                                getTypeOfChangeByChangeCodeAndTypeOfThing(
                                  change.code,
                                  'argument',
                                );

                              return (
                                <li key={JSON.stringify(change)}>
                                  <DetailedChangeIcon
                                    changeType={argChangeType}
                                    className="mr-2"
                                  />
                                  <ChangeHighlighter
                                    size="small"
                                    className="mr-2"
                                    typeOfChange={argChangeType}
                                  >
                                    {change.argNode?.name}
                                  </ChangeHighlighter>{' '}
                                  <span className="text-base lowercase text-secondary">
                                    {change.code.replace(/_/g, ' ')}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            },
          )}
        </ul>
      </div>
    </div>
  );
};

const StyledChangeDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={classnames('text-base lowercase text-secondary', className)}
    {...props}
  />
);
